/**
 * "Put the chart down" — a one-way command from the page shell to the Workbench.
 *
 * Something outside the chart has taken over the screen (the mobile chat sheet, the
 * mobile navigation drawer), so whatever the user had half-open on the canvas — an
 * armed tool, a trade idea in edit mode with its strip and note box, a pane
 * dialog — should close. The Workbench answers by running the teardown it already
 * uses when the walkthrough takes the canvas.
 *
 * Why a signal rather than more z-index. The workbench's chrome is a tall band of
 * root-level z-indexes (the idea rim, the edit strip, guides, note boxes) and every
 * new overlay elsewhere in the app would otherwise have to be raised above all of
 * it, forever. Standing the canvas down is the honest fix: it removes the thing
 * being overlapped instead of restacking it. The z bands are still ordered
 * sensibly as a backstop for the frame in between, and for the chrome this does not
 * cover (hover cards, the context menu).
 *
 * Nothing is lost by obeying it: drawings and trade-idea marks persist the moment
 * they land, so closing edit mode keeps the work — the same as picking another tool.
 *
 * An EDGE (a monotonic token), not a boolean. It is a command with no "resume", and
 * a boolean would have to be reset by someone, which is the kind of bookkeeping that
 * strands state when a surface unmounts unexpectedly.
 */

let token = 0
const listeners = new Set<() => void>()

export const canvasSuspend = {
  /** Ask the chart to close whatever it has open. Safe when nothing is mounted. */
  request() {
    token += 1
    listeners.forEach((l) => l())
  },

  subscribe(l: () => void): () => void {
    listeners.add(l)
    return () => listeners.delete(l)
  },

  /** Test helper: the number of requests so far. */
  count(): number {
    return token
  },
}
